import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { studentsService, type Student, type BulkImportResult } from "@/lib/services";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, Search, Trash2, Upload, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const CLASS_ORDER = ["Nursery", "JKG", "SKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

// Tolerant class-label matching (mirrors backend core.class_name_matches):
// "Grade 5A", "Grade 5", "5A" and "5" all refer to the same class.
function parseClassLabel(label: string) {
  const s = (label || "").trim().replace(/^(grade|class|std|standard)\s+/i, "").trim();
  const m = s.match(/^(\d+)\s*[-\s]?\s*([A-Za-z])?$/);
  if (m) return { grade: m[1], section: (m[2] || "").toUpperCase() || null };
  return { grade: s, section: null as string | null };
}
export function classLabelsMatch(a?: string | null, b?: string | null): boolean {
  const pa = parseClassLabel(a || ""), pb = parseClassLabel(b || "");
  if (pa.grade.toLowerCase() !== pb.grade.toLowerCase()) return false;
  if (pa.section && pb.section) return pa.section === pb.section;
  return true; // grade matches, section-tolerant
}

function classSortKey(c: string): number {
  const idx = CLASS_ORDER.indexOf(c);
  return idx === -1 ? 99 + c.charCodeAt(0) : idx;
}

/** Client-side downscale to a max dimension. Keeps aspect ratio and encodes as JPEG. */
async function resizeImage(file: File, maxDim = 512, quality = 0.8): Promise<File> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(bitmap, 0, 0, w, h);
  const blob: Blob = await new Promise((res) => canvas.toBlob((b) => res(b || file), "image/jpeg", quality));
  return new File([blob], (file.name.replace(/\.[^.]+$/, "") || "photo") + ".jpg", { type: "image/jpeg" });
}

export function StudentsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "principal";
  const isTeacher = user?.role === "teacher";
  // Student/parent get a scoped, read-only "Child Profile" view (backend returns
  // only their linked student record).
  const isChildScope = user?.role === "parent" || user?.role === "student";
  const { data: students = [], isPending } = useQuery({
    queryKey: ["students"], queryFn: () => studentsService.list(),
    enabled: isAdmin || isTeacher || isChildScope,
  });
  const [q, setQ] = useState("");
  const [classFilter, setClassFilter] = useState<string>("");
  const [editing, setEditing] = useState<Student | null>(null);
  const [creating, setCreating] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const scoped = useMemo(() => {
    // Backend already scopes the list for teachers to their class, so use as-is.
    return students;
  }, [students]);

  const classes = useMemo(() => {
    const set = new Set<string>();
    scoped.forEach((s) => s.class_name && set.add(s.class_name));
    return Array.from(set).sort((a, b) => classSortKey(a) - classSortKey(b));
  }, [scoped]);

  if (!isAdmin && !isTeacher && !isChildScope) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground">
        <div className="text-lg font-semibold">Student roster is restricted</div>
        <div className="text-sm">You do not have access to the student directory.</div>
      </div>
    );
  }

  const filtered = scoped.filter((s) => {
    if (classFilter && s.class_name !== classFilter) return false;
    if (!q) return true;
    const term = q.toLowerCase();
    return (
      s.name.toLowerCase().includes(term) ||
      (s.email || "").toLowerCase().includes(term) ||
      (s.gr_no || s.roll_no || "").toString().includes(q) ||
      (s.father_name || "").toLowerCase().includes(term) ||
      (s.phone || "").includes(q)
    );
  });

  // A teacher is the "class teacher" for a student iff their assigned class matches (tolerant).
  const canEdit = (s: Student) => isAdmin || (isTeacher && classLabelsMatch(user?.class_name, s.class_name));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {user?.role === "parent" ? "Child Profile" : user?.role === "student" ? "My Profile" : "Students"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isChildScope
              ? "A read-only view of your record. Contact the school office for corrections."
              : isTeacher
              ? `${scoped.length} students in ${user?.class_name || "your class"} — you can edit your class`
              : `${students.length} students enrolled${classFilter ? ` • ${filtered.length} in ${classFilter}` : ""}`}
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="mr-2 h-4 w-4" /> Import Excel
            </Button>
            <Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" /> New student</Button>
          </div>
        )}
      </div>

      {!isChildScope && (
        <div className="flex flex-wrap gap-3">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search by name, GR no, father, phone…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          {classes.length > 0 && (
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All classes</option>
              {classes.map((c) => (
                <option key={c} value={c}>Class {c}</option>
              ))}
            </select>
          )}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {isPending ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="max-h-[calc(100vh-320px)] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="w-14"></TableHead>
                    <TableHead>GR No.</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Sec</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Father</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((s) => (
                    <TableRow key={s.id} className="hover:bg-muted/50">
                      <TableCell><Avatar student={s} size={36} /></TableCell>
                      <TableCell><Badge variant="secondary">{s.gr_no || s.roll_no || "—"}</Badge></TableCell>
                      <TableCell className="font-semibold">{s.name}</TableCell>
                      <TableCell>{s.class_name}</TableCell>
                      <TableCell className="text-muted-foreground">{s.section || "—"}</TableCell>
                      <TableCell className="capitalize text-muted-foreground">{s.gender || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{s.father_name || s.parent_name || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{s.phone || "—"}</TableCell>
                      <TableCell><Badge variant="outline">{s.social_category || "—"}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>
                          {canEdit(s) ? "Edit" : "View"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground">
                      {isChildScope ? "No profile linked to your account yet. Please contact the school office." : "No students match your filter."}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {creating && <StudentDialog canEdit onClose={() => setCreating(false)} />}
      {editing && <StudentDialog student={editing} canEdit={canEdit(editing)} onClose={() => setEditing(null)} />}
      {importOpen && <ImportExcelDialog onClose={() => setImportOpen(false)} />}
    </div>
  );
}

function Avatar({ student, size = 40 }: { student: Student; size?: number }) {
  const initials = (student.name || "?")
    .split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("");
  if (student.photo_base64) {
    return (
      <img src={student.photo_base64} alt={student.name}
        style={{ width: size, height: size }}
        className="rounded-full object-cover border border-border" />
    );
  }
  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      className="flex items-center justify-center rounded-full bg-primary/10 font-semibold text-primary"
    >
      {initials || "?"}
    </div>
  );
}

function FormInput({ label, value, onChange, type = "text", placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string;
}) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <Input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function StudentDialog({ student, canEdit = true, onClose }: { student?: Student; canEdit?: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "principal";
  const [f, setF] = useState<any>(student || {
    name: "", roll_no: "", gr_no: "", class_name: "", section: "",
    date_of_birth: "", gender: "male",
    email: "", phone: "", phone_2: "",
    father_name: "", mother_name: "",
    address_line_1: "", address_line_2: "", address_line_3: "",
    state: "GUJARAT", pin_code: "",
    social_category: "GEN", aadhaar_no: "", name_as_per_aadhaar: "",
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = { ...f };
      if (!payload.roll_no) payload.roll_no = payload.gr_no || "";
      if (!payload.parent_name) payload.parent_name = payload.father_name;
      if (!payload.parent_phone) payload.parent_phone = payload.phone;
      return student ? studentsService.update(student.id, payload) : studentsService.create(payload);
    },
    onSuccess: () => { toast.success(student ? "Student updated" : "Student created"); qc.invalidateQueries({ queryKey: ["students"] }); onClose(); },
    onError: (e: any) => toast.error(e.message || "Save failed"),
  });
  const del = useMutation({
    mutationFn: () => studentsService.remove(student!.id),
    onSuccess: () => { toast.info("Student removed"); qc.invalidateQueries({ queryKey: ["students"] }); onClose(); },
    onError: (e: any) => toast.error(e.message),
  });

  const setField = (k: string, v: string) => setF({ ...f, [k]: v });

  const photoInput = useRef<HTMLInputElement>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const uploadPhoto = async (file: File) => {
    if (!student) { toast.error("Save the student first before uploading a photo"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    setPhotoUploading(true);
    try {
      const resized = await resizeImage(file, 512, 0.8);
      const updated = await studentsService.uploadPhoto(student.id, resized);
      setF((prev: any) => ({ ...prev, photo_base64: updated.photo_base64 }));
      qc.invalidateQueries({ queryKey: ["students"] });
      toast.success("Photo uploaded");
    } catch (e: any) {
      toast.error(e.message || "Upload failed");
    } finally {
      setPhotoUploading(false);
    }
  };
  const removePhoto = async () => {
    if (!student) return;
    try {
      await studentsService.removePhoto(student.id);
      setF((prev: any) => ({ ...prev, photo_base64: null }));
      qc.invalidateQueries({ queryKey: ["students"] });
      toast.info("Photo removed");
    } catch (e: any) {
      toast.error(e.message || "Delete failed");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{student ? student.name : "New student"}</DialogTitle>
          {student && <DialogDescription>GR No. {student.gr_no || "—"} • Class {student.class_name}{student.section ? `-${student.section}` : ""}{!canEdit && " • Read-only"}</DialogDescription>}
        </DialogHeader>
        <fieldset disabled={!canEdit} className={!canEdit ? "opacity-90" : ""}>
        <div className="space-y-4">
          {/* Photo section — only relevant once the student exists */}
          {student && (
            <section className="flex items-center gap-4 rounded-md border border-dashed border-border p-3">
              {f.photo_base64 ? (
                <img src={f.photo_base64} alt={f.name}
                  className="h-24 w-24 rounded-md object-cover border border-border" />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-md bg-primary/10 text-2xl font-semibold text-primary">
                  {(f.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s: string) => s[0]?.toUpperCase() || "").join("") || "?"}
                </div>
              )}
              <div className="flex flex-col gap-2">
                <div className="text-sm font-semibold">Student photo</div>
                {canEdit && (
                  <>
                    <p className="text-xs text-muted-foreground">
                      JPG or PNG · Auto-resized to 512px · Max 500 KB after resize.
                    </p>
                    <div className="flex gap-2">
                      <input ref={photoInput} type="file" accept="image/*" hidden
                        onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadPhoto(file); e.target.value = ""; }} />
                      <Button type="button" size="sm" variant="outline" disabled={photoUploading}
                        onClick={() => photoInput.current?.click()}>
                        {photoUploading ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Uploading</> : (f.photo_base64 ? "Replace" : "Upload photo")}
                      </Button>
                      {f.photo_base64 && (
                        <Button type="button" size="sm" variant="ghost" disabled={photoUploading} onClick={removePhoto}>
                          <Trash2 className="mr-1 h-3 w-3 text-destructive" /> Remove
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </section>
          )}

          <section>
            <h3 className="text-sm font-semibold text-primary mb-2">Personal</h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <FormInput label="Full name" value={f.name || ""} onChange={(v) => setField("name", v)} />
              <FormInput label="GR No." value={f.gr_no || ""} onChange={(v) => setField("gr_no", v)} />
              <FormInput label="Roll No." value={f.roll_no || ""} onChange={(v) => setField("roll_no", v)} />
              <FormInput label="Class" value={f.class_name || ""} onChange={(v) => setField("class_name", v)} placeholder="e.g. 5 or JKG" />
              <FormInput label="Section" value={f.section || ""} onChange={(v) => setField("section", v)} placeholder="A/B/C" />
              <FormInput label="Date of birth" value={f.date_of_birth || ""} onChange={(v) => setField("date_of_birth", v)} type="date" />
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Gender</Label>
                <select value={f.gender || ""}
                  onChange={(e) => setF({ ...f, gender: e.target.value })}
                  className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">—</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Social Category</Label>
                <select value={f.social_category || ""}
                  onChange={(e) => setF({ ...f, social_category: e.target.value })}
                  className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">—</option>
                  <option value="GEN">GEN</option>
                  <option value="OBC">OBC</option>
                  <option value="SC">SC</option>
                  <option value="ST">ST</option>
                  <option value="EWS">EWS</option>
                </select>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-primary mb-2">Contact</h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <FormInput label="Phone 1" value={f.phone || ""} onChange={(v) => setField("phone", v)} />
              <FormInput label="Phone 2" value={f.phone_2 || ""} onChange={(v) => setField("phone_2", v)} />
              <FormInput label="Email" value={f.email || ""} onChange={(v) => setField("email", v)} type="email" />
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-primary mb-2">Parents</h3>
            <div className="grid grid-cols-2 gap-3">
              <FormInput label="Father's name" value={f.father_name || ""} onChange={(v) => setField("father_name", v)} />
              <FormInput label="Mother's name" value={f.mother_name || ""} onChange={(v) => setField("mother_name", v)} />
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-primary mb-2">Address</h3>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <FormInput label="Line 1" value={f.address_line_1 || ""} onChange={(v) => setField("address_line_1", v)} />
              <FormInput label="Line 2" value={f.address_line_2 || ""} onChange={(v) => setField("address_line_2", v)} />
              <FormInput label="Line 3" value={f.address_line_3 || ""} onChange={(v) => setField("address_line_3", v)} />
              <FormInput label="State" value={f.state || ""} onChange={(v) => setField("state", v)} />
              <FormInput label="Pin code" value={f.pin_code || ""} onChange={(v) => setField("pin_code", v)} />
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-primary mb-2">AADHAAR</h3>
            <div className="grid grid-cols-2 gap-3">
              <FormInput label="AADHAAR No." value={f.aadhaar_no || ""} onChange={(v) => setField("aadhaar_no", v)} />
              <FormInput label="Name as per AADHAAR" value={f.name_as_per_aadhaar || ""} onChange={(v) => setField("name_as_per_aadhaar", v)} />
            </div>
          </section>
        </div>
        </fieldset>

        <DialogFooter>
          {student && isAdmin && (
            <Button variant="outline" onClick={() => { if (confirm("Delete student?")) del.mutate(); }} disabled={del.isPending}>
              <Trash2 className="mr-2 h-4 w-4 text-destructive" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>{canEdit ? "Cancel" : "Close"}</Button>
          {canEdit && (
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportExcelDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);

  const importer = useMutation({
    mutationFn: () => studentsService.importExcel(file!, replaceExisting),
    onSuccess: (r) => {
      setResult(r);
      toast.success(`Imported ${r.created} new, updated ${r.updated}, skipped ${r.skipped}`);
      qc.invalidateQueries({ queryKey: ["students"] });
    },
    onError: (e: any) => toast.error(e.message || "Import failed"),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" /> Import students from Excel
          </DialogTitle>
          <DialogDescription>
            Upload the LFS student workbook. Expected sheets: G1..G12, G-NUR, G-JKG, G-SKG. Rows are deduped by GR No.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" hidden
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
          <Button variant="outline" className="w-full" onClick={() => fileRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" /> {file ? file.name : "Choose .xlsx file"}
          </Button>

          <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
            <input type="checkbox" className="mt-1" checked={replaceExisting}
              onChange={(e) => setReplaceExisting(e.target.checked)} />
            <span>
              <span className="font-medium">Update matching records</span>
              <span className="block text-xs text-muted-foreground">
                If a student with the same GR No. already exists, update their fields instead of skipping.
              </span>
            </span>
          </label>

          {result && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm dark:border-emerald-900/50 dark:bg-emerald-950/30">
              <div className="flex items-center gap-2 font-semibold text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="h-4 w-4" /> Import complete
              </div>
              <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                <Stat label="Total" v={result.total} />
                <Stat label="Created" v={result.created} tone="ok" />
                <Stat label="Updated" v={result.updated} tone="ok" />
                <Stat label="Skipped" v={result.skipped} tone="muted" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => importer.mutate()} disabled={!file || importer.isPending}>
            {importer.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload & Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, v, tone }: { label: string; v: number; tone?: "ok" | "muted" }) {
  const color = tone === "ok" ? "text-emerald-600" : tone === "muted" ? "text-muted-foreground" : "text-foreground";
  return (
    <div>
      <div className={`text-xl font-bold ${color}`}>{v}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
